import { useNavigate } from "react-router-dom";
import { useReportsMetadataQuery, useActiveRefreshesQuery } from "../../../hooks/reports/queries/useGroupQueries";
import { useReportSelectionRules } from "../../../hooks/reports/useReportSelectionRules";
import { useModalStore } from "../../../store/report/modalStore";
import { useReportViewStore } from "../../../store/report/useReportViewStore";
import { useReportBuilderStore } from "../../../store/report/useReportBuilderStore";
import { DELETE_REPORT, REVERT_DRAFT } from "../../../constants/reportActions";
import Button from "../../UI/Button";
import { reportsDashboardEnabled } from "../../../utils/features";

const SidePanelToolbar = () => {
  const navigate = useNavigate();
  const isSidePanelOpen = useReportViewStore((s) => s.isSidePanelOpen);
  const toggleSidePanel = useReportViewStore((s) => s.toggleSidePanel);
  const selectedReports = useReportViewStore((s) => s.selectedReports);
  const clearSelection = useReportViewStore((s) => s.clearSelection);
  const resetBuilder = useReportBuilderStore((s) => s.resetBuilder);
  
  const openModal = useModalStore((s) => s.openModal);
  const { data: reportsData } = useReportsMetadataQuery();
  const { data: activeRefreshes } = useActiveRefreshesQuery(reportsDashboardEnabled);
  const reports = reportsData ?? { bookmarkedReports: [], recentReports: [] };
  const { canGroup, canUngroup } = useReportSelectionRules(selectedReports, reports);

  const singleSelected = selectedReports.length === 1
    ? (() => {
        for (const item of reports.bookmarkedReports) {
          if (item.type === "group") {
            const found = item.reports.find(r => r.id === selectedReports[0]);
            if (found) return found;
          } else {
            if (item.id === selectedReports[0]) return item;
          }
        }
        return null;
      })()
    : null;

  // True if any selected report belongs to a group that is currently refreshing.
  // Both grouping and ungrouping are blocked in this case - you cannot move
  // reports into or out of a group while it is refreshing.
  const isGroupingLocked = reportsDashboardEnabled && (activeRefreshes?.refreshingGroups.some(groupId =>
    reports.bookmarkedReports.some(item =>
      item.type === "group" &&
      item.id === groupId &&
      item.reports.some(r => selectedReports.includes(r.id))
    )
  ) ?? false);

  const handleNewReport = () => {
    resetBuilder();
    navigate("/reports/new");
  };

  return (
    <div className="side-panel-toolbar">
      <div className="toolbar-top-row">
        <button 
          className="button flat side-panel-icon-button toggle-btn" 
          onClick={toggleSidePanel}
          title={isSidePanelOpen ? "Collapse Menu" : "Expand Menu"}
        >
          <i className="ri-menu-fill" />
        </button>

        <Button 
          text="New Report" 
          id="side-panel-new-report" 
          className="primary-action-btn"
          onClick={handleNewReport} 
          icon={<i className={`ri-add-line${isSidePanelOpen ? " margin-end-2" : ""}`} />}
          iconLocation="start"
        />
      </div>

      <div className={`toolbar-selection-actions${!(isSidePanelOpen && !isGroupingLocked && (canGroup || canUngroup)) ? " hidden" : ""}`}>
          {singleSelected && (
            <>
              <button
                className="button secondary side-panel-icon-button"
                title="Rename"
                onClick={() => openModal("renameReport", { id: singleSelected.id, name: singleSelected.name })}
              >
                <i className="ri-pencil-line" />
              </button>
              <button
                className="button secondary side-panel-icon-button"
                title={singleSelected.isDraft ? "Revert / Discard" : "Delete"}
                onClick={() => openModal("deleteReport", {
                  id: singleSelected.id,
                  action: singleSelected.isDraft ? REVERT_DRAFT : DELETE_REPORT,
                  parentReportId: singleSelected.parentReportId,
                })}
              >
                <i className="ri-delete-bin-line" />
              </button>
            </>
          )}

          {canGroup && (
            <button
              className="button secondary side-panel-icon-button"
              title="Add to Group"
              onClick={() => openModal("groupReports", { reportIds: selectedReports, groupName: "" })}
            >
              <i className="ri-folder-add-fill" />
            </button>
          )}

          {canUngroup && (
            <button
              className="button secondary side-panel-icon-button"
              title="Ungroup Selected"
              onClick={() => openModal("ungroupReports", { reportIds: selectedReports })}
            >
              <i className="ri-folder-reduce-fill" />
            </button>
          )}

          <button
            className="button flat side-panel-icon-button"
            title="Deselect All"
            onClick={clearSelection}
          >
            <i className="ri-close-line" />
          </button>
      </div>
    </div>
  );
};

export default SidePanelToolbar;
